import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'

// Containers
const DefaultContainer = () => import('@/containers/DefaultContainer')
const SystemContainer = () => import('@/containers/SystemContainer')

// System Views
const SystemDashboard = () => import('@/views/system/Dashboard')
const Accounts = () => import('@/views/system/accounts/Accounts')
const Account = () => import('@/views/system/accounts/Account')
const SystemNumbers = () => import('@/views/system/numbers/Numbers')
const SystemNumber = () => import('@/views/system/numbers/Number')
const SystemRoles = () => import('@/views/system/Roles')

// Views
const Dashboard = () => import('@/views/Dashboard')
const Numbers = () => import('@/views/calling/numbers/Numbers')
const Number = () => import('@/views/calling/numbers/Number')
const Extensions = () => import('@/views/phones/Extensions/Extensions')
const Extension = () => import('@/views/phones/Extensions/Extension')
const Devices = () => import('@/views/phones/Devices/Devices')
const Device = () => import('@/views/phones/Devices/Device')
const Groups = () => import('@/views/phones/Groups/Groups')
const Group = () => import('@/views/phones/Groups/Group')
const Mailboxes = () => import('@/views/voicemail/Mailboxes')
const Mailbox = () => import('@/views/voicemail/Mailbox')
const Menus = () => import('@/views/calling/menus/Menus')
const Menu = () => import('@/views/calling/menus/Menu')
const Locations = () => import('@/views/settings/Locations/Locations')
const Location = () => import('@/views/settings/Locations/Location')
const Recordings = () => import('@/views/calling/recordings/Recordings')
const Recording = () => import('@/views/calling/recordings/Recording')
const RoutingProfiles = () => import('@/views/calling/routing/RoutingProfiles')
const RoutingProfile = () => import('@/views/calling/routing/RoutingProfile')
const Schedules = () => import('@/views/calling/schedules/Schedules')
const Schedule = () => import('@/views/calling/schedules/Schedule')
const Users = () => import('@/views/settings/Users/Users')
const User = () => import('@/views/settings/Users/User')

const Colors = () => import('@/views/theme/Colors')
const Typography = () => import('@/views/theme/Typography')

const Charts = () => import('@/views/Charts')
const Widgets = () => import('@/views/Widgets')

// Views - Components
const Cards = () => import('@/views/base/Cards')
const Forms = () => import('@/views/base/Forms')
const Switches = () => import('@/views/base/Switches')
const Tables = () => import('@/views/base/Tables')
const Tabs = () => import('@/views/base/Tabs')
const Breadcrumbs = () => import('@/views/base/Breadcrumbs')
const Carousels = () => import('@/views/base/Carousels')
const Collapses = () => import('@/views/base/Collapses')
const Jumbotrons = () => import('@/views/base/Jumbotrons')
const ListGroups = () => import('@/views/base/ListGroups')
const Navs = () => import('@/views/base/Navs')
const Navbars = () => import('@/views/base/Navbars')
const Paginations = () => import('@/views/base/Paginations')
const Popovers = () => import('@/views/base/Popovers')
const ProgressBars = () => import('@/views/base/ProgressBars')
const Tooltips = () => import('@/views/base/Tooltips')

// Views - Buttons
const StandardButtons = () => import('@/views/buttons/StandardButtons')
const ButtonGroups = () => import('@/views/buttons/ButtonGroups')
const Dropdowns = () => import('@/views/buttons/Dropdowns')
const BrandButtons = () => import('@/views/buttons/BrandButtons')

// Views - Icons
const Flags = () => import('@/views/icons/Flags')
const FontAwesome = () => import('@/views/icons/FontAwesome')
const SimpleLineIcons = () => import('@/views/icons/SimpleLineIcons')
const CoreUIIcons = () => import('@/views/icons/CoreUIIcons')

// Views - Notifications
const Alerts = () => import('@/views/notifications/Alerts')
const Badges = () => import('@/views/notifications/Badges')
const Modals = () => import('@/views/notifications/Modals')

// Views - Pages
const Page404 = () => import('@/views/pages/Page404')
const Page500 = () => import('@/views/pages/Page500')
const Login = () => import('@/views/pages/Login')
const Register = () => import('@/views/pages/Register')

// Users
// const Users = () => import('@/views/users/Users')
// const User = () => import('@/views/users/User')

Vue.use(Router)

let router = new Router({
  mode: 'hash', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'open active',
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: Login
    },
    {
      path: '/system',
      redirect: '/system/dashboard',
      name: 'System',
      component: SystemContainer,
      meta: {
        requiredAuth: true,
        can: {
          action: 'view',
          subject: 'systemSettings'
        }
      },
      children: [
        {
          path: '/system/dashboard',
          name: 'System Dashboard',
          component: SystemDashboard
        },
        {
          path: '/system/accounts',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              name: 'Accounts',
              component: Accounts,
            },
            {
              path: ':id',
              meta: { label: 'Account Details'},
              name: 'Account',
              component: Account,
            }
          ]
        },
        {
          path: '/system/numbers',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              name: 'Numbers',
              component: SystemNumbers,
            },
            {
              path: ':id',
              meta: { label: 'Number Details'},
              name: 'Number',
              component: SystemNumber,
            }
          ]
        },
        {
          path: '/system/roles',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              name: 'Roles',
              component: SystemRoles,
            }
          ]
        },
      ]
    },
    {
      path: '/',
      redirect: '/dashboard',
      name: 'Home',
      component: DefaultContainer,
      meta: {
        requiresAuth: true
      },
      children: [
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: Dashboard
        },
        {
          path: 'voicemail',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              name: 'Voicemail',
              component: Mailboxes,
            },
            {
              path: ':id',
              meta: { label: 'Mailbox Details'},
              name: 'Mailbox',
              component: Mailbox,
            }
          ]
        },
        {
          path: 'theme',
          redirect: '/theme/colors',
          name: 'Theme',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'colors',
              name: 'Colors',
              component: Colors
            },
            {
              path: 'typography',
              name: 'Typography',
              component: Typography
            }
          ]
        },
        {
          path: 'charts',
          name: 'Charts',
          component: Charts
        },
        {
          path: 'widgets',
          name: 'Widgets',
          component: Widgets
        },
        // {
        //   path: 'users',
        //   meta: { label: 'Users'},
        //   component: {
        //     render (c) { return c('router-view') }
        //   },
        //   children: [
        //     {
        //       path: '',
        //       component: Users,
        //     },
        //     {
        //       path: ':id',
        //       meta: { label: 'User Details'},
        //       name: 'User',
        //       component: User,
        //     },
        //   ]
        // },
        {
          path: 'base',
          redirect: '/base/cards',
          name: 'Base',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'cards',
              name: 'Cards',
              component: Cards
            },
            {
              path: 'forms',
              name: 'Forms',
              component: Forms
            },
            {
              path: 'switches',
              name: 'Switches',
              component: Switches
            },
            {
              path: 'tables',
              name: 'Tables',
              component: Tables
            },
            {
              path: 'tabs',
              name: 'Tabs',
              component: Tabs
            },
            {
              path: 'breadcrumbs',
              name: 'Breadcrumbs',
              component: Breadcrumbs
            },
            {
              path: 'carousels',
              name: 'Carousels',
              component: Carousels
            },
            {
              path: 'collapses',
              name: 'Collapses',
              component: Collapses
            },
            {
              path: 'jumbotrons',
              name: 'Jumbotrons',
              component: Jumbotrons
            },
            {
              path: 'list-groups',
              name: 'List Groups',
              component: ListGroups
            },
            {
              path: 'navs',
              name: 'Navs',
              component: Navs
            },
            {
              path: 'navbars',
              name: 'Navbars',
              component: Navbars
            },
            {
              path: 'paginations',
              name: 'Paginations',
              component: Paginations
            },
            {
              path: 'popovers',
              name: 'Popovers',
              component: Popovers
            },
            {
              path: 'progress-bars',
              name: 'Progress Bars',
              component: ProgressBars
            },
            {
              path: 'tooltips',
              name: 'Tooltips',
              component: Tooltips
            }
          ]
        },
        {
          path: 'buttons',
          redirect: '/buttons/standard-buttons',
          name: 'Buttons',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'standard-buttons',
              name: 'Standard Buttons',
              component: StandardButtons
            },
            {
              path: 'button-groups',
              name: 'Button Groups',
              component: ButtonGroups
            },
            {
              path: 'dropdowns',
              name: 'Dropdowns',
              component: Dropdowns
            },
            {
              path: 'brand-buttons',
              name: 'Brand Buttons',
              component: BrandButtons
            }
          ]
        },
        {
          path: 'icons',
          redirect: '/icons/font-awesome',
          name: 'Icons',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'coreui-icons',
              name: 'CoreUI Icons',
              component: CoreUIIcons
            },
            {
              path: 'flags',
              name: 'Flags',
              component: Flags
            },
            {
              path: 'font-awesome',
              name: 'Font Awesome',
              component: FontAwesome
            },
            {
              path: 'simple-line-icons',
              name: 'Simple Line Icons',
              component: SimpleLineIcons
            }
          ]
        },
        {
          path: 'notifications',
          redirect: '/notifications/alerts',
          name: 'Notifications',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'alerts',
              name: 'Alerts',
              component: Alerts
            },
            {
              path: 'badges',
              name: 'Badges',
              component: Badges
            },
            {
              path: 'modals',
              name: 'Modals',
              component: Modals
            }
          ]
        }
      ]
    },
    {
      path: '/',
      redirect: '/calling',
      name: 'Calling',
      component: DefaultContainer,
      children: [
        {
          path: '/calling/numbers',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              name: 'Numbers',
              component: Numbers,
            },
            {
              path: ':id',
              meta: { label: 'Number Details'},
              name: 'Number',
              component: Number,
            }
          ]
        },
        {
          path: '/calling/menus',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              name: 'Menus',
              component: Menus,
            },
            {
              path: ':id',
              meta: { label: 'Menu Details'},
              name: 'Menu',
              component: Menu,
            }
          ]
        },
        {
          path: '/calling/routing',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              name: 'Routing Profiles',
              component: RoutingProfiles,
            },
            {
              path: ':id',
              meta: { label: 'Routing Profile Details'},
              name: 'Routing Profile',
              component: RoutingProfile,
            }
          ]
        },
        {
          path: '/calling/recordings',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              name: 'Recordings',
              component: Recordings,
            },
            {
              path: ':id',
              meta: { label: 'Recording Details'},
              name: 'Recording',
              component: Recording,
            }
          ]
        },
        {
          path: '/calling/schedules',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              name: 'Schedules',
              component: Schedules,
            },
            {
              path: ':id',
              meta: { label: 'Schedule Details'},
              name: 'Schedule',
              component: Schedule,
            }
          ]
        },
      ]
    },
    {
      path: '/',
      redirect: '/phones',
      name: 'Phones',
      component: DefaultContainer,
      children: [
        {
          path: '/phones/extensions',
          meta: { label: 'Extensions' },
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Extensions,
            },
            {
              path: ':id',
              meta: { label: 'Extension Details'},
              name: 'Extension',
              component: Extension,
            }
          ]
        },
        {
          path: '/phones/groups',
          meta: { label: 'Groups' },
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Groups,
            },
            {
              path: ':id',
              meta: { label: 'Group Details'},
              name: 'Group',
              component: Group,
            }
          ]
        },
        {
          path: '/phones/devices',
          meta: { label: 'Devices' },
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Devices,
            },
            {
              path: ':id',
              meta: { label: 'Device Details'},
              name: 'Device',
              component: Device,
            }
          ]
        }
      ]
    },
    {
      path: '/',
      redirect: '/settings',
      name: 'Settings',
      component: DefaultContainer,
      children: [
        {
          path: '/settings/locations',
          meta: { label: 'Locations'},
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Locations,
            },
            {
              path: ':id',
              meta: { label: 'Location Details'},
              name: 'Location',
              component: Location,
            }
          ]
        },
        {
          path: '/settings/users',
          meta: { label: 'Users'},
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: '',
              component: Users,
            },
            {
              path: ':id',
              meta: { label: 'User Details'},
              name: 'User',
              component: User,
            }
          ]
        },
      ]
    },
    {
      path: '/pages',
      redirect: '/pages/404',
      name: 'Pages',
      component: {
        render (c) { return c('router-view') }
      },
      children: [
        {
          path: '404',
          name: 'Page404',
          component: Page404
        },
        {
          path: '500',
          name: 'Page500',
          component: Page500
        },
        {
          path: 'register',
          name: 'Register',
          component: Register
        }
      ]
    }
  ]
})

router.beforeResolve((to, from, next) => {
  const loggedIn = store.getters['global/loggedIn']

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!loggedIn) {
      next({
          path: '/login'
      })
    } else {
      next()
    }
  } else if(to.matched.some(record => record.meta.authRedirect)) {
    if (loggedIn) {
      next(to.meta.authRedirect)
    } else {
      next()
    }
  } else {
    next()
  }
})

// const loginConfirmed = to => {
//   const loggedIn = store.getters['global/loggedIn']

//   return (loggedIn || to.name === 'Login')
// }

export default router
